import { API_BASE } from "../api/config";
import { getSharedIdToken } from "../api/authToken";
import { validateMsiCustomerContract } from "./customerContract";
import { loadMsiCustomerState } from "./customerContract";
import { useEffect, useState } from "react";

export const MSI_ADMIN_CURRENT_URL = `${API_BASE}/admin/msi/current`;
const SHA256 = /^[0-9a-f]{64}$/;
const VERSION = /^sha256-[0-9a-f]{24}-v1$/;
const cache = { versionId: "", checksum: "", release: null };

export function msiRuntimeSource(locationRef = globalThis.location) {
  const protocol = String(locationRef?.protocol || "");
  const hostname = String(locationRef?.hostname || "").toLowerCase();
  const port = String(locationRef?.port || "");
  return protocol === "http:" && ["localhost", "127.0.0.1"].includes(hostname) && port === "3000"
    ? "LOCAL_STATIC"
    : "AUTHENTICATED_ADMIN";
}

export function clearMsiAdminReleaseCache() {
  cache.versionId = "";
  cache.checksum = "";
  cache.release = null;
}

const authenticated = async (url, fetchImpl, options = {}) => {
  const token = await getSharedIdToken();
  return fetchImpl(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` },
  });
};

const jsonResponse = async (response, code) => {
  if (!response?.ok || response.status !== 200) throw new Error(`${code}_${response?.status || "NO_RESPONSE"}`);
  if (!/json/i.test(response.headers?.get?.("content-type") || "")) throw new Error(`${code}_CONTENT_TYPE`);
  return response.json();
};

const sha256Hex = async (bytes) => {
  if (!globalThis.crypto?.subtle) throw new Error("MSI_CHECKSUM_UNAVAILABLE");
  const value = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(value)].map((part) => part.toString(16).padStart(2, "0")).join("");
};

export async function fetchMsiAdminRelease(fetchImpl = fetch, { signal } = {}) {
  const pointer = await jsonResponse(await authenticated(`${MSI_ADMIN_CURRENT_URL}?_=${Date.now()}-${Math.random().toString(36).slice(2)}`, fetchImpl, { cache: "no-store", signal }), "MSI_POINTER_UNAVAILABLE");
  if (pointer.contract_version !== "PWG_MSI_POINTER_1.0" || pointer.classification !== "PRIVATE_ADMIN" || !VERSION.test(pointer.version_id || "") || !SHA256.test(pointer.source_sha256 || "") || pointer.release_path !== `msi/releases/${pointer.version_id}` || pointer.manifest_path !== `${pointer.release_path}/manifest.json`) throw new Error("INVALID_MSI_POINTER");
  if (cache.versionId === pointer.version_id && cache.checksum === pointer.source_sha256 && cache.release) return cache.release;
  const version = encodeURIComponent(pointer.version_id);
  const manifest = await jsonResponse(await authenticated(`${API_BASE}/admin/msi/releases/${version}/manifest`, fetchImpl, { signal }), "MSI_MANIFEST_UNAVAILABLE");
  if (manifest.contract_version !== "PWG_MSI_ADMIN_1.0" || manifest.classification !== "PRIVATE_ADMIN" || manifest.version_id !== pointer.version_id || manifest.source_sha256 !== pointer.source_sha256 || manifest.release_path !== pointer.release_path || manifest.contract?.path !== "msi-contract.json" || manifest.contract?.sha256 !== pointer.source_sha256) throw new Error("INVALID_MSI_MANIFEST");
  const response = await authenticated(`${API_BASE}/admin/msi/releases/${version}/contract`, fetchImpl, { signal });
  if (!response?.ok || response.status !== 200) throw new Error(`MSI_CONTRACT_UNAVAILABLE_${response?.status || "NO_RESPONSE"}`);
  const bytes = await response.arrayBuffer();
  const checksum = await sha256Hex(bytes);
  if (checksum !== pointer.source_sha256 || checksum !== manifest.contract.sha256 || bytes.byteLength !== Number(manifest.contract.bytes)) throw new Error("MSI_CONTRACT_INTEGRITY_FAILURE");
  const contract = validateMsiCustomerContract(JSON.parse(new TextDecoder().decode(bytes)));
  if (contract.run_id !== pointer.run_id || contract.source_as_of !== pointer.source_as_of || contract.structures.length !== Number(pointer.structure_count)) throw new Error("MSI_RELEASE_ACCOUNTING_FAILURE");
  const release = { pointer, manifest, contract: { ...contract, freshness_status: pointer.freshness_status } };
  cache.versionId = pointer.version_id;
  cache.checksum = pointer.source_sha256;
  cache.release = release;
  return release;
}

export function monitorMsiAdminRelease({ onRelease, onError, fetchImpl = fetch, intervalMs = 60000, documentRef = document, windowRef = window } = {}) {
  let stopped = false; let timer = null; let inflight = null; let generation = 0; let accepted = "";
  const schedule = () => { clearTimeout(timer); if (!stopped && documentRef.visibilityState !== "hidden") timer = setTimeout(check, intervalMs); };
  const check = async () => {
    if (stopped || inflight || documentRef.visibilityState === "hidden") return;
    const token = ++generation; const controller = new AbortController(); inflight = controller;
    try {
      const release = await fetchMsiAdminRelease(fetchImpl, { signal: controller.signal });
      if (!stopped && token === generation && release.pointer.version_id !== accepted) { accepted = release.pointer.version_id; onRelease?.(release); }
    } catch (error) { if (!stopped && error?.name !== "AbortError") onError?.(error); }
    finally { if (inflight === controller) inflight = null; schedule(); }
  };
  const visible = () => { if (documentRef.visibilityState === "hidden") clearTimeout(timer); else check(); };
  const focus = () => check();
  documentRef.addEventListener("visibilitychange", visible); windowRef.addEventListener("focus", focus); check();
  return () => { stopped = true; generation += 1; clearTimeout(timer); inflight?.abort(); documentRef.removeEventListener("visibilitychange", visible); windowRef.removeEventListener("focus", focus); };
}

export function useMsiAdminRelease() {
  const [state, setState] = useState({ status: "loading", contract: null, release: null, warning: "" });
  useEffect(() => {
    if (msiRuntimeSource() === "LOCAL_STATIC") {
      let active = true;
      loadMsiCustomerState(fetch).then((result) => {
        if (!active) return;
        setState(result.status === "ready"
          ? { status: "ready", contract: result.contract, release: { contract: result.contract, source: "LOCAL_STATIC" }, warning: "" }
          : { status: result.status, contract: result.contract, release: null, warning: "Local governed MSI contract unavailable" });
      });
      return () => { active = false; };
    }
    let accepted = null;
    const stop = monitorMsiAdminRelease({
      onRelease: (release) => {
        accepted = release;
        setState({ status: "ready", contract: release.contract, release, warning: "" });
      },
      onError: (error) => setState((prior) => accepted
        ? { ...prior, warning: error?.message || "MSI refresh failed" }
        : { status: "unavailable", contract: null, release: null, warning: error?.message || "MSI unavailable" }),
    });
    return () => { accepted = null; stop(); clearMsiAdminReleaseCache(); };
  }, []);
  return state;
}
